import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

export default function CaseSummaryButton({ child }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setOpen(true);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/case-summary", { child_id: child.id });
      setSummary(data.summary);
    } catch (err) {
      toast.error(formatApiError(err));
      setOpen(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline" size="sm"
        className="h-9 text-xs border-dashed border-primary/50 text-primary"
        data-testid="child-ai-summary-button"
        onClick={run}
        disabled={busy}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Sparkles className="h-3.5 w-3.5 mr-1.5" />}
        Summarise case with AI
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="child-ai-summary-dialog">
          <DialogHeader>
            <DialogTitle>Case summary — {child.first_name} {child.surname}</DialogTitle>
            <DialogDescription>
              AI-generated factual brief from recorded data — not an assessment or recommendation.
              Safeguarding records are never included.
            </DialogDescription>
          </DialogHeader>
          {busy ? (
            <div className="flex items-center justify-center gap-2 py-10 text-slate-600" data-testid="child-ai-summary-loading">
              <Loader2 className="h-4 w-4 animate-spin" /> Generating summary…
            </div>
          ) : (
            <div className="whitespace-pre-wrap text-sm text-slate-800 max-h-[60vh] overflow-y-auto" data-testid="child-ai-summary-text">
              {summary}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
